import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { Role } from './useValidateSession';
import type { UserPermission } from './useEditDefaultPermissions';
import type { EnabledNotificationTypesDict } from './useGetProfile';

export interface LocationEntry {
    _id: string;
    subscriptionActive: boolean;
    title: string;
    city: string;
    state: string;
}

/** Per-location emails notified when an inventory purchase order is placed for that location. Multiple rows may share a locationId. */
export interface PartFulfillmentContact {
    locationId: string;
    email: string;
}

interface LocationSection {
    key: 'location';
    label: string;
    data: Array<LocationEntry>;
}

/** Computed by settings API from users.active / incomplete userInvites. Not stored in Mongo. */
export type UserStatus = 'pending' | 'active' | 'inactive';

export interface UserEntry {
    _id: string;
    email: string;
    position: Role;
    /** Present on active users; pending invites may omit it. */
    username?: string;
    locationId: Array<string> | 'all';
    /** Prefer this over `active`. Absent on older API responses. */
    status?: UserStatus;
    firstName?: string | null;
    lastName?: string | null;
    admin?: boolean;
    /** Present on real user rows for backwards compatibility; prefer `status`. */
    active?: boolean;
    /** Invite `_id` when status is pending. */
    inviteId?: string;
    /** Invite timestamp ISO string when status is pending. */
    invitedAt?: string;
    /** Per-user Ask Carl grant (Mongo); not managed in settings UI. */
    askCarlAllowed?: boolean;
    permissions: UserPermission | 'admin' | 'default';
    enabledNotificationTypes?: EnabledNotificationTypesDict;
    notificationsEnabled?: boolean;
}

export function getUserStatus(user: UserEntry): UserStatus {
    if (user.status) return user.status;
    if (user.active === false) return 'inactive';
    return 'active';
}

interface UserSection {
    key: 'user';
    label: string;
    defaultPermission: Record<Role, UserPermission>;
    data: Array<UserEntry>;
}

interface SubscriptionSection {
    key: 'subscription';
    label: string;
    data: {
        numLocations: number;
        startDate: Date | null;
        billingDate: Date | null;
        hasTrialed: boolean;
    };
}

export interface BillingCard {
    brand: string | null;
    last4: string | null;
    expMonth: number | null;
    expYear: number | null;
}

export interface SubscriptionSummaryItem {
    priceId: string;
    productName: string | null;
    quantity: number | null;
    /** Stripe unit amount in the smallest currency unit (e.g. cents). */
    unitAmount: number | null;
    currency: string;
    interval: string | null;
}

export interface SettingsSubscription {
    id: string;
    status: string;
    productName: string | null;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: number | null;
    /** The total the customer will be charged on their next invoice, in the smallest currency unit. */
    amount: number;
    currency: string;
}

export interface SubscriptionsSettingsData {
    customer: {
        id: string;
        email: string | null;
        name: string | null;
        defaultPaymentMethod: BillingCard | null;
    } | null;
    subscriptions: Array<SettingsSubscription>;
}

interface SubscriptionsSection {
    key: 'subscriptions';
    label: string;
    data: SubscriptionsSettingsData;
}

export interface ITSetupSection {
    key: 'it-setup';
    label: string;
    data: {
        inboundEmail: string | null;
        adminEmail: string | null;
        clientPartstraderEmail: string | null;
        setupType: 'single-server' | 'multi-server' | 'no-server' | null;
        serverAccounts: Array<{
            locationId: string | 'all';
            locationName: string;
            email: string;
            password: string;
        }>;
    };
}

export interface QuoteRequirement {
    supplierId: string; // from Supplier
    name: string;
    makes: Array<{ _id: string; title: string; }>;
    locations: Array<{ _id: string; title: string; }> | 'all';
}

type RequoteOption = 'always' | 're-quote' | 'never';
export interface QuotingSettings {
    timeBounds: {
        collisionLink: { min: number; max: number; };
        partstrader: { min: number; max: number; };
    };
    skipQuoting: {
        drp: RequoteOption;
        nonDrp: RequoteOption;
    };
    requote: Array<QuoteRequirement>;
    autoQuoteStaleOrders: boolean;
    allowExpedited: 'always' | 'never' | 'last-week' | 'last-two-weeks';
    allowDealerReferral: boolean;
    requoteIncorrectPartNumbers: 'all' | 'partial' | 'off';
    allowScheduledRequests: boolean;
    cutoff: { hour: number; minute: number; };
}

interface QuotingSection {
    key: 'quoting';
    label: string;
    data: QuotingSettings & { hasCl: boolean; hasPt: boolean; };
}

export type PartType = 'oem' | 'oe-surplus' | 'a/m' | 'rcy' | 're-man' | 're-con';

/** Acceptable Margin Threshold row: required `default` plus optional per–part-type overrides. */
export type FlagMarginBelowEntry = {
    partType: 'default' | PartType;
    type: 'dollar' | 'percentage';
    value: number; // percent stored 0–1; dollar as absolute $
};

/** One side of a CCC ordering rule: named records, or every one of them. */
export type CccOrderingScope = Array<{ _id: string; name: string; }> | 'all';

/** One custom CCC ordering rule: which makes, for which carriers. */
export interface CccOrderingRule {
    makes: CccOrderingScope;
    carriers: CccOrderingScope;
}

/**
 * Always, never, or custom rules — CCC is used when a job matches any rule.
 *
 * The single `{ makes, carriers }` object is unchanged and is still what one
 * rule is stored as. The list is additive: it is only written once a shop has
 * a second rule, which is what lets carriers carry different OEMs (one shared
 * object ANDs every make with every carrier). The API accepts both.
 */
export type CccOrdering = 'always' | 'never' | CccOrderingRule | Array<CccOrderingRule>;

export interface OrderingSettings {
    allowAutoOrder: boolean;
    autoOrderDisabledLocations: Array<{ _id: string; title: string; }>;
    disableAutoOrderCustomerPay: boolean;
    flagMarginThreshhold: number;
    nonDrpFlagMarginThreshold: number;
    flagNonPreferredVendors: boolean;
    flagMarginBelowByPart: Array<FlagMarginBelowEntry>;
    autoAcceptSupplierChanges: boolean;
    autoAcceptNonPriceSupplierChangesOnly: boolean;
    cccOrdering: CccOrdering;
}

export type AftermarketCertification = 'nsf' | 'capa' | 'ds' | 'pp' | 'keysiq';

interface PriceMatchMatrixEntryBase {
    partType?: PartType;
    aftermarketCertifications?: Array<AftermarketCertification>; // must have part type
    supplierId?: string; // in conjuction w supplier name
    supplierName?: string; // in conjunction w supplier id
    source: Array<'partstrader'>;
}
type PriceMatchMatrixEntry = PriceMatchMatrixEntryBase & (
    | { partType: PartType; }
    | { supplierId: string; }
    | { source: Array<'partstrader'>; }
);

export interface InsuranceDrpSettings {
    insurerId: string;
    insurerName: string;
    locations: Array<{ _id: string; title: string; }> | 'all';
    prohibitedSuppliers: Array<{ _id: string; title: string; }>;
    note: string;
    priceMatchEstimate: boolean;
    priceMatchCCC: boolean;
    priceMatchPartstrader: boolean;
    priceMatchCustom: boolean;
    priceMatchCustomRanking?: Array<PriceMatchMatrixEntry>;
    orderingThreshold?: number;
}

export interface InsuranceOption {
    _id: string;
    name: string;
}

interface InsuranceSection {
    key: 'insurance';
    label: string;
    data: Array<InsuranceDrpSettings & { partstraderRequired: boolean; }>;
}

interface OrderingSection {
    key: 'ordering';
    label: string;
    managementSystem: 'ccc' | 'nex';
    /** Delivery receive opt-in date (org setting delivery.receivingEnabledAt); null when not opted in. */
    receivingEnabledAt: string | null;
    /** NEX purchase-order integration opt-in (Beta Features). */
    nexOrdering?: NexOrderingSettings;
    /** Whether the org has a Nexsyis tenant credential; the NEX toggle is inert without it. */
    nexIntegrationAvailable?: boolean;
    data: OrderingSettings;
}

export interface NexOrderingSettings {
    enabled: boolean;
    locationIds: string[];
    enabledAt: string | null;
}

export type VendorType = 'preferred' | 'avoid' | 'required' | 'super-preferred' | 'prohibited';
export interface OrgVendor {
    supplierId: string;
    name: string;
    formattedAddress?: string;
    discount?: number;
    directOrdering?: 'opstrax' | 'collisionlink' | null;
    isDealer: boolean;
    ptFee?: number;
    marginBump?: number;
    makes: Array<{ _id: string; name: string; }> | 'all';
    avoidLocations: Array<{ _id: string; title: string; }> | 'all';
    prohibitedLocations: Array<{ _id: string; title: string; }> | 'all';
    preferredLocations: Array<{ _id: string; title: string; }> | 'all';
    superPreferredLocations: Array<{ _id: string; title: string; }> | 'all';
    requiredLocations: Array<{ _id: string; title: string; }> | 'all';
}

export type OrderingMarketplace = 'opstrax' | 'partstrader' | 'ccc' | 'collisionlink';
export interface OEMSettings {
    makeId: string;
    name: string;
    certifications: Array<{ _id: string; title: string; }> | 'all';
    drpThreshold: null | number;
    nonDrpThreshold: null | number;
    minQuotingTime: null | number;
    rebate?: {
        requiredMarketplaces: Array<Exclude<OrderingMarketplace, 'ccc'>>;
        thressholds: Array<{
            minThresshold: number; // (0, 1)
            maxThresshold: number; // (0, 1)
            rebate: number;
        }>;
        overrideDrpThresshold: boolean;
    };
}

interface OEMSection {
    key: 'oem';
    label: string;
    data: Array<OEMSettings>;
}

interface VendorSection {
    key: 'vendor';
    label: string;
    data: Array<OrgVendor>;
}

export interface GeneralPart {
    categoryId: number;
    categoryName: string;
    subcategoryId: number;
    subcategoryName: string;
    partTerminologyId: number;
    partTerminologyName: string;
    positionIds: number[];
    positionGroupName: string;
}

export interface AllowablePartType {
    partType: PartType;
    certifications?: Array<AftermarketCertification>;
}

export interface PartRule {
    categoryId: number;
    categoryName: string;
    subcategoryId: number;
    subcategoryName: string;
    partTerminologyId: number;
    partTerminologyName: string;
    positionIds: number[];
    positionGroupName: string;
    allowablePartTypes: Array<AllowablePartType>;
    makes?: 'all' | Array<{ _id: string; name: string; }>;
    prohibitedInsurance?: Array<{ _id: string; name: string; }> | 'all';
}

export interface OlderVehiclePreference {
    enabled: boolean;
    minVehicleAgeYears: number;
}

export interface OemOnlyBelowPriceThreshold {
    enabled: boolean;
    maxBuyPrice: number;
}

export type SystemGeneratedNoteApplication = 'marketplace' | 'estimating' | 'both';

export type SystemGeneratedNoteKey =
    | 'oem-endorsement'
    | 'head-tail-lamps-with-logo'
    | 'oe-warranty'
    | 'welded-on-parts'
    | 'one-time-use-part'
    | 'incorrect-part-quoted'
    | 'vendor-misquoted-quantity'
    | 'invalid-pricing'
    | 'adhesive-templates'
    | 'srs-oem-only'
    | 'mileage-year-restricted'
    | 'adas-restricted'
    | 'insurer-guidelines'
    | 'supplier-disallowed'
    | 'used-cooling-component'
    | 'one-time-use-glass'
    | 'not-insurance-quality'
    | 'delivery-too-long'
    | 'invalid-part'
    | 'state-guidelines'
    | 'cannot-utilize-lkq'
    | 'cannot-utilize-recycled'
    | 'cannot-utilize-aftermarket'
    | 'cannot-utilize-recon'
    | 'cannot-utilize-prohibited'
    | 'cannot-utilize-assembly'
    | 'price-matched-to-assembly';

export interface SystemGeneratedNoteConfig {
    key: SystemGeneratedNoteKey;
    situation: string;
    note: string;
    drpInsuranceIds: Array<string> | 'all';
    nonDrpInsuranceIds: Array<string> | 'all';
    customerPayEnabled: boolean;
    applicationDefault: SystemGeneratedNoteApplication;
}

export interface AlwaysPreferOemOverride {
    locations: Array<{ _id: string; title: string; }> | 'all';
    maxMargin: number;
    minPrice: number;
}

export interface SelectionSettings {
    preferredDeliveryTime: number;
    maximumDeliveryTime: number;
    flagWheels: boolean;
    /** Auto-flag LKQ/rcy parts used for price matching. Missing/undefined treated as false. */
    flagLkqPriceMatched?: boolean;
    /** Auto-flag whenever an LKQ/rcy part is selected. Missing/undefined treated as false. */
    flagLkqSelected?: boolean;
    alwaysPreferOem: boolean;
    alwaysPreferOemOverrides: Array<AlwaysPreferOemOverride>;
    treatRemanRcyAsOem: boolean;
    alwaysPreferCapa: boolean;
    canOrderBackordered: boolean;
    nonDrpPriceMatch: 'estimate' | Array<PriceMatchMatrixEntry>;
    nonDrpMirrorEstimateTypes: boolean;
    rcySourceVehicleRestriction: boolean;
    supplierConsolidation: { type: 'dollar' | 'percentage'; value: number; };
    allowedPartRules: Array<PartRule>;
    forbiddenPartRules: Array<PartRule>;
    flaggedPartRules: Array<PartRule>;
    priceMatchToAllowableThreshold: number;
    allowableFlagThreshold: number;
    olderVehiclePreference?: OlderVehiclePreference;
    oemOnlyBelowPriceThreshold?: OemOnlyBelowPriceThreshold;
    systemGeneratedNotes?: Array<SystemGeneratedNoteConfig>;
}

export interface RekeySettings {
    shouldRekey: boolean;
    shouldRekeyNonDrp: boolean;
    autoRekeyQuoteOnly: boolean;
    cutoff: { hour: number; minute: number; };
    excludedMakes: Array<{ makeId: string; name: string; }>;
    createManualPO: boolean;
    /** When true (default), attempt A/M cert (CAPA/KEYSIQ) changes on rekey and flag if they fail. */
    flagAftermarketCertifications: boolean;
    modifyPriceMatchedDescriptions: { enabled: boolean; text: string; };
    /** Prepend CAPA to CAPA A/M part descriptions. Only applied when flagAftermarketCertifications is on. */
    addPartCertification: boolean;
    apuMaximization: {
        default: PartType,
        custom: Array<{
            insuranceCompanies: Array<{ _id: string; name: string; }>;
            partType: PartType;
        }>;
    };
}

/** @deprecated Legacy flat rows; use `SupplierVendorNexAliasGroup`. */
export interface InvoiceVendorNexAlias {
    invoiceAlias: string;
    nexVendorName: string;
}

export interface NexVendorMapping {
    vendorId?: number;
    vendorName: string;
}

export interface SupplierVendorNexAliasGroup {
    supplierId: string;
    supplierTitle: string;
    invoiceTextAliases: string[];
    nexVendorMappings: NexVendorMapping[];
}

export interface FlaggedSpecialVendor {
    supplierId: string;
    supplierName?: string;
}

export interface VendorFlaggingRule {
    supplierId: string;
    supplierName?: string;
    invoiceType: 'received' | 'returned' | 'return_slip' | 'sublet' | '*';
    unmatchedLineItemsNexSubmitMode:
    | 'dont_allow'
    | 'allow_manual'
    | 'allow_manual_and_automatic';
    quantityMismatchFlaggingEnabled: boolean;
    priceMismatchFlaggingEnabled: boolean;
    amountMismatchFlaggingEnabled: boolean;
    amountMismatchPercentThreshold?: number;
    amountMismatchBaseDollarThreshold?: number;
    /** Absolute difference that flags lines at or above the switchover. Unset keeps the base dollar. */
    amountMismatchDollarThreshold?: number;
    /** When set, overrides the org both-directions setting. Unset inherits it. */
    amountMismatchFlagBothDirections?: boolean;
    requireManualReview: boolean;
}

/** Used (PAL) and aftermarket (PAA) markup row in org settings. */
export type PricingPartKind = 'used' | 'aftermarket' | 'oem' | 'glass' | 'reman' | '*';

export interface LkqMarkup {
    insuranceCompanyId: string;
    supplierId: string;
    state: string;
    markupPercentage: number;
    partKind?: PricingPartKind;
    make?: string;
    insuranceCompanyName?: string;
    supplierName?: string;
}

/** Used (PAL) and aftermarket (PAA) discount row in org settings. */
export interface LkqDiscount {
    insuranceCompanyId: string;
    supplierId: string;
    state: string;
    discountPercentage: number;
    partKind?: PricingPartKind;
    make?: string;
    insuranceCompanyName?: string;
    supplierName?: string;
}

export interface SubletMarkup {
    insuranceCompanyId: string;
    supplierId: string;
    state: string;
    markupPercentage: number;
    insuranceCompanyName?: string;
    supplierName?: string;
}

export interface SubletDiscount {
    insuranceCompanyId: string;
    supplierId: string;
    state: string;
    discountPercentage: number;
    insuranceCompanyName?: string;
    supplierName?: string;
}

/** Expected list→buy discount by insurer, vendor, state, and vehicle make (Discount flag settings). */
export interface DiscountFlagExpectation {
    insuranceCompanyId: string;
    supplierId: string;
    state: string;
    expectedDiscountPercentage: number;
    /** Missing matches every vehicle make. */
    make?: string;
    insuranceCompanyName?: string;
    supplierName?: string;
}

export interface LocationDefaultTaxRate {
    locationId: string;
    /** Tax rate as a 0..1 decimal (e.g. 0.0825 for 8.25%). For Canadian locations this is the GST/HST rate. */
    taxRate?: number;
    /** PST rate as a 0..1 decimal. Only applicable for Canadian locations with split tax. */
    pstTaxRate?: number;
    applyToReceived: boolean;
    /** Applies to `returned` (credited) invoices. */
    applyToCredited: boolean;
    applyToSublet: boolean;
}

export interface InvoiceInboxTypeAddresses {
    received: string;
    returned: string;
    return_slip: string;
    sublet: string;
}

export interface InvoiceInboxAddresses extends InvoiceInboxTypeAddresses {
    bulk: InvoiceInboxTypeAddresses;
}

export interface InvoicingSettings {
    freightAsLineItem: boolean;
    /**
     * When true, US sales tax is sent as an AP distribution line (`Sales Tax`, manualLineInd 2)
     * and AP header tax is zeroed. Canada / split tax is unaffected. Default false.
     */
    salesTaxAsLineItem?: boolean;
    vendorNexAliases?: InvoiceVendorNexAlias[];
    vendorNexAliasMappings?: SupplierVendorNexAliasGroup[];
    /** Canonical suppliers flagged for invoice review (matched via internalSupplierId after NEX reconcile). */
    flaggedSpecialVendors?: FlaggedSpecialVendor[];
    /** Per-vendor overrides of org-wide flagging and unmatched-submit settings. */
    vendorFlaggingRules?: VendorFlaggingRule[];
    emailInvoicingOptIn?: boolean;
    invoiceInboxBaseLocalPart?: string;
    invoiceInboxAddresses?: InvoiceInboxAddresses;
    emailSetupGuideUrl?: string;
    inboundDefaultLocationId?: string;
    receivedAmountMismatchPercentThreshold?: number;
    returnedAmountMismatchPercentThreshold?: number;
    receivedAmountMismatchBaseDollarThreshold?: number;
    returnedAmountMismatchBaseDollarThreshold?: number;
    /** Absolute difference for received/sublet lines at or above the switchover. Unset uses the base dollar. */
    receivedAmountMismatchDollarThreshold?: number;
    /** Absolute difference for credited lines at or above the switchover. Unset uses the base dollar. */
    returnedAmountMismatchDollarThreshold?: number;
    /** When false, amount-mismatch flags are not produced/shown and do not block auto-submit. Default true. */
    amountMismatchFlaggingEnabled?: boolean;
    /**
     * When false, amount mismatches flag only a higher invoice total (or a smaller credit).
     * Default true, which flags both directions.
     */
    amountMismatchFlagBothDirections?: boolean;
    /** When false, quantity-mismatch flags are not produced/shown and do not block auto-submit. Default true. */
    quantityMismatchFlaggingEnabled?: boolean;
    /** When false, price-mismatch flags are not produced/shown and do not block auto-submit. Default true. */
    priceMismatchFlaggingEnabled?: boolean;
    /**
     * Which NEX PO-line quantity to use for matching, suggestions, and quantity flags (received/sublet).
     * Return invoices always use returned quantity. Default `qtyRec`.
     */
    nexLineQuantityBasis?: 'qtyRec' | 'qtyOnOrder';
    /** How shop reference on invoices maps to NEX purchasing APIs (admin invoicing setting). */
    nexReconciliationIdentifierMode?: 'ro' | 'po' | 'both';
    /** Default used (PAL) and aftermarket (PAA) markup when no insurance/vendor/state row matches (0..1). */
    defaultLkqMarkupPercentage?: number;
    /**
     * When true, used/aftermarket markup keeps the invoice list price when extraction found one (> 0).
     * Default false.
     */
    preferInvoiceListPriceOverLkqMarkup?: boolean;
    /** Default sublet markup when no insurance/vendor/state row matches (0..1). */
    defaultSubletMarkupPercentage?: number;
    /**
     * When true, sublet markup keeps the invoice list price when extraction found one (> 0).
     * Default false.
     */
    preferInvoiceListPriceOverSubletMarkup?: boolean;
    lkqMarkups?: LkqMarkup[];
    lkqDiscounts?: LkqDiscount[];
    subletMarkups?: SubletMarkup[];
    subletDiscounts?: SubletDiscount[];
    discountFlagExpectations?: DiscountFlagExpectation[];
    /** Per-shop-location default tax rates applied to applicable invoices at processing time. */
    locationDefaultTaxRates?: LocationDefaultTaxRate[];
    /**
     * Whether billable lines without a NEX PO line match may be submitted (`manualLineInd`).
     * Always requires at least one matched billable line.
     */
    unmatchedLineItemsNexSubmitMode?:
    | 'dont_allow'
    | 'allow_manual'
    | 'allow_manual_and_automatic';
}

/** Wire format for `POST /settings/update` invoicing section (ids only, no display names). */
export interface InvoicingSettingsUpdatePayload
    extends Omit<
        InvoicingSettings,
        | 'flaggedSpecialVendors'
        | 'vendorFlaggingRules'
        | 'lkqMarkups'
        | 'lkqDiscounts'
        | 'subletMarkups'
        | 'subletDiscounts'
        | 'discountFlagExpectations'
    > {
    flaggedSpecialVendors?: string[];
    vendorFlaggingRules?: Array<
        Omit<VendorFlaggingRule, 'supplierName'>
    >;
    lkqMarkups?: Array<Pick<LkqMarkup, 'insuranceCompanyId' | 'supplierId' | 'state' | 'markupPercentage' | 'partKind' | 'make'>>;
    lkqDiscounts?: Array<Pick<LkqDiscount, 'insuranceCompanyId' | 'supplierId' | 'state' | 'discountPercentage' | 'partKind' | 'make'>>;
    subletMarkups?: Array<
        Pick<SubletMarkup, 'insuranceCompanyId' | 'supplierId' | 'state' | 'markupPercentage'>
    >;
    subletDiscounts?: Array<
        Pick<SubletDiscount, 'insuranceCompanyId' | 'supplierId' | 'state' | 'discountPercentage'>
    >;
    discountFlagExpectations?: Array<
        Pick<
            DiscountFlagExpectation,
            'insuranceCompanyId' | 'supplierId' | 'state' | 'expectedDiscountPercentage' | 'make'
        >
    >;
}

interface RekeySection {
    key: 'rekey';
    label: string;
    managementSystem: 'ccc' | 'nex';
    data: RekeySettings;
}

interface InvoicingSection {
    key: 'invoicing';
    label: string;
    data: InvoicingSettings;
}

interface SelectionSection {
    key: 'selection';
    label: string;
    data: SelectionSettings;
}

export interface PriceMatchSettings {
    autoPriceMatch: boolean;
}

interface PriceMatchSection {
    key: 'ems';
    label: string;
    data: PriceMatchSettings;
}

interface IntegrationsSection {
    key: 'integrations';
    label: string;
    data: { enabled: true; };
}

export type Section =
    LocationSection |
    UserSection |
    SubscriptionSection |
    SubscriptionsSection |
    ITSetupSection |
    IntegrationsSection |
    QuotingSection |
    OrderingSection |
    VendorSection |
    InsuranceSection |
    OEMSection |
    RekeySection |
    InvoicingSection |
    PriceMatchSection |
    SelectionSection;

/** Nested under top-level `inventory` on settings (not inventoryEnabled). */
export interface InventoryBusinessLineOption {
    name: string;
}

/** How remaining cost lots are allocated. Default 'lifo' when missing. */
export type BuyPriceMethod = 'lifo' | 'fifo';

export interface OrgInventorySettings {
    businessLines: Array<InventoryBusinessLineOption>;
    /** Must match a businessLines[].name when set. */
    defaultBusinessLine?: string | null;
    partFulfillmentContacts?: Array<PartFulfillmentContact>;
    buyPriceMethod?: BuyPriceMethod;
}

export interface SettingsResponse {
    oemOptions: Array<{
        _id: string;
        name: string;
        similarMakes: Array<{
            _id: string;
            name: string;
        }>;
    }>;
    nonDrpInsuranceOptions: Array<InsuranceOption>;
    /** Org inventory settings (business lines, part fulfillment contacts, etc.). Separate from inventoryEnabled. */
    inventory?: OrgInventorySettings;
    inventoryEnabled: boolean;
    invoicingEnabled: boolean;
    topSection?: {
        title: "Organization Settings";
        sections: Array<Section>;
    };
    bottomSection?: {
        title: "Preferences";
        sections: Array<Section>;
    };
}

const getSettings = async (sessionId: string): Promise<SettingsResponse> => {
    const { data } = await axios.post(`${API_URL}/settings/detail`,
        {},
        {
            headers: {
                Authorization: sessionId
            }
        }
    );

    return data;
};

export const useGetSettings = () => {
    const sessionContext = useContext(SessionContext);
    return useQuery({
        queryKey: ['settings', sessionContext?.organizationId ?? ''],
        queryFn: () => getSettings(sessionContext?.sessionId ?? ''),
        enabled: !!sessionContext?.sessionId,
    });
}; 